import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { IconCheck, IconChevronDown, IconClose, IconCopy, IconWarning } from './icons.js';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export * from './icons.js';

// ------------------------------ Button ------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

/**
 * สีของปุ่มมาจาก CSS variable ไม่ใช่คลาส `dark:` คู่ ๆ
 * ทำให้เปลี่ยนธีมทั้งแอปได้จากที่เดียว และไม่มีโอกาสลืมใส่คู่มืดของคลาสใดคลาสหนึ่ง
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    'text-[var(--brand-ink)] bg-[var(--brand)] shadow-[var(--shadow-sm)] ' +
    'hover:brightness-[1.08] active:brightness-95 disabled:opacity-50',
  secondary:
    'text-[var(--ink)] bg-[var(--sunken)] border border-[var(--line)] ' +
    'hover:bg-[var(--surface)] hover:border-[var(--ink-3)]',
  outline:
    'text-[var(--ink-2)] border border-[var(--line)] bg-transparent ' +
    'hover:text-[var(--ink)] hover:border-[var(--ink-3)] hover:bg-[var(--sunken)]',
  ghost:
    'text-[var(--ink-2)] bg-transparent hover:text-[var(--ink)] hover:bg-[var(--sunken)]',
  danger:
    'text-white bg-[var(--bad)] shadow-[var(--shadow-sm)] hover:brightness-110 disabled:opacity-50',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-9.5 px-4 text-sm gap-2 rounded-[10px]',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-xl',
};

export function Button({
  variant = 'primary', size = 'md', loading, icon, className, children, disabled, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant; size?: Size; loading?: boolean; icon?: ReactNode;
}) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex shrink-0 items-center justify-center font-medium',
        'transition-[background,color,border-color,filter,box-shadow] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant], SIZES[size], className,
      )}
      style={{ height: size === 'md' ? 38 : undefined, ...rest.style }}
    >
      {(loading || icon) && (
        <span className="grid h-4 w-4 shrink-0 place-items-center leading-none [&>svg]:block [&>svg]:max-h-full [&>svg]:max-w-full">
          {loading ? <Spinner className="h-4 w-4" /> : icon}
        </span>
      )}
      {children && <span className="min-w-0 truncate">{children}</span>}
    </button>
  );
}

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" className="opacity-25" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ------------------------------ Fields ------------------------------

const FIELD =
  'w-full rounded-[10px] border px-3 text-sm transition-colors ' +
  'bg-[var(--surface)] border-[var(--line)] text-[var(--ink)] ' +
  'placeholder:text-[var(--ink-3)] ' +
  'hover:border-[var(--ink-3)] ' +
  'focus:border-[var(--brand)] focus:outline-none ' +
  'focus:ring-4 focus:ring-[color-mix(in_srgb,var(--brand)_18%,transparent)] ' +
  'disabled:bg-[var(--sunken)] disabled:text-[var(--ink-3)] disabled:cursor-not-allowed';

export function Field({
  label, error, hint, required, children, htmlFor,
}: { label?: string; error?: string | null; hint?: string; required?: boolean; children: ReactNode; htmlFor?: string }) {
  return (
    <div>
      {label && (
        <label className="label" htmlFor={htmlFor}>
          {label}
          {required && <span className="ml-0.5 text-[var(--bad)]" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="err">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

export function Input({
  label, error, hint, className, ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string | null; hint?: string }) {
  const id = useId();
  return (
    <Field label={label} error={error} hint={hint} required={rest.required} htmlFor={rest.id ?? id}>
      <input
        {...rest}
        id={rest.id ?? id}
        aria-invalid={error ? true : undefined}
        className={cx(FIELD, 'h-[38px]', error && '!border-[var(--bad)] focus:!ring-[color-mix(in_srgb,var(--bad)_18%,transparent)]', className)}
      />
    </Field>
  );
}

export function Textarea({
  label, error, hint, className, ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; error?: string | null; hint?: string }) {
  const id = useId();
  return (
    <Field label={label} error={error} hint={hint} required={rest.required} htmlFor={rest.id ?? id}>
      <textarea
        {...rest}
        id={rest.id ?? id}
        aria-invalid={error ? true : undefined}
        className={cx(FIELD, 'min-h-[88px] resize-y py-2.5 leading-relaxed', error && '!border-[var(--bad)]', className)}
      />
    </Field>
  );
}

export function Select({
  label, error, hint, className, children, ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; error?: string | null; hint?: string }) {
  const id = useId();
  return (
    <Field label={label} error={error} hint={hint} required={rest.required} htmlFor={rest.id ?? id}>
      <div className="relative">
        <select
          {...rest}
          id={rest.id ?? id}
          className={cx(FIELD, 'h-[38px] cursor-pointer appearance-none pr-9', className)}
        >
          {children}
        </select>
        <IconChevronDown
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]"
        />
      </div>
    </Field>
  );
}

export function Toggle({
  checked, onChange, label, hint, disabled,
}: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <label className={cx('flex items-start gap-3', disabled ? 'opacity-55' : 'cursor-pointer')}>
      <button
        type="button" role="switch" aria-checked={checked} disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative mt-px h-[22px] w-[38px] shrink-0 rounded-full border transition-colors duration-150',
          // สถานะปิดต้องมีเส้นขอบ ไม่งั้นบนพื้นมืดจะกลืนกับการ์ดจนดูไม่ออกว่ามีสวิตช์อยู่
          checked
            ? 'border-[var(--brand)] bg-[var(--brand)]'
            : 'border-[var(--line)] bg-[var(--sunken)]',
        )}
      >
        <span
          className={cx(
            'absolute left-[2px] top-1/2 h-4 w-4 rounded-full shadow-sm transition-transform duration-150',
            checked ? 'bg-white' : 'bg-[var(--ink-3)]',
          )}
          /*
           * เขียน transform ตรง ๆ แทนคลาส translate ของ Tailwind ด้วยสองเหตุผล:
           *   1. ต้องระบุ left ให้ชัด ไม่งั้นเบราว์เซอร์คิด static position ให้เอง (ได้ 18px)
           *      แล้ว translate ไปบวกทับ ทำให้ปุ่มกลมหลุดออกนอกกรอบจนมองไม่เห็น
           *   2. Tailwind ประกอบ translate-x กับ -translate-y ผ่านตัวแปร CSS ตัวเดียวกัน
           *      พอเปลี่ยนเฉพาะค่าตัวแปร transform ที่คำนวณจริงไม่ขยับตาม ปุ่มเลยค้างที่เดิม
           */
          style={{ transform: `translate(${checked ? 16 : 0}px, -50%)` }}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[var(--ink)]">{label}</span>
        {hint && <span className="mt-0.5 block text-xs leading-relaxed text-[var(--ink-3)]">{hint}</span>}
      </span>
    </label>
  );
}

export function ColorInput({
  label, value, onChange, hint,
}: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <div className="flex gap-2">
        <label
          className="relative h-[38px] w-11 shrink-0 cursor-pointer overflow-hidden rounded-[10px] border border-[var(--line)]"
          style={{ background: value }}
        >
          <input
            type="color" id={id} value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </label>
        <input
          type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className={cx(FIELD, 'h-[38px] font-mono text-xs uppercase tracking-wide')}
          maxLength={7} spellCheck={false}
        />
      </div>
    </Field>
  );
}

// ------------------------------ Display ------------------------------

const TONES = {
  gray:   'bg-[var(--sunken)] text-[var(--ink-2)] ring-[var(--line)]',
  blue:   'bg-[var(--brand-soft)] text-[var(--brand)] ring-[color-mix(in_srgb,var(--brand)_22%,transparent)]',
  green:  'bg-[var(--ok-soft)] text-[var(--ok)] ring-[color-mix(in_srgb,var(--ok)_22%,transparent)]',
  amber:  'bg-[var(--warn-soft)] text-[var(--warn)] ring-[color-mix(in_srgb,var(--warn)_22%,transparent)]',
  rose:   'bg-[var(--bad-soft)] text-[var(--bad)] ring-[color-mix(in_srgb,var(--bad)_22%,transparent)]',
  violet: 'bg-[var(--info-soft)] text-[var(--info)] ring-[color-mix(in_srgb,var(--info)_22%,transparent)]',
} as const;

export type Tone = keyof typeof TONES;

export function Badge({ tone = 'gray', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cx(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-[3px]',
      'text-[11px] font-semibold leading-5 ring-1 ring-inset',
      TONES[tone], className,
    )}>
      {children}
    </span>
  );
}

export const STATUS_TONE: Record<string, Tone> = {
  PUBLISHED: 'green', ACTIVE: 'green',
  DRAFT: 'gray', ARCHIVED: 'gray',
  IN_REVIEW: 'amber', SCHEDULED: 'violet', TRIAL: 'amber', PAUSED: 'amber',
  SUSPENDED: 'rose', CANCELLED: 'rose',
};

export function Card({
  children, className, hover, ...rest
}: { children: ReactNode; className?: string; hover?: boolean } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cx(
        'surface',
        hover && 'transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[var(--ink-3)] hover:shadow-[var(--shadow-md)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title, description, actions, breadcrumb,
}: { title: string; description?: ReactNode; actions?: ReactNode; breadcrumb?: ReactNode }) {
  return (
    <div className="mb-7">
      {breadcrumb}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-[26px] font-bold leading-tight tracking-[-.02em] text-[var(--ink)]">
            {title}
          </h1>
          {description && (
            <div className="mt-1.5 text-sm leading-relaxed text-[var(--ink-3)]">{description}</div>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon, title, description, action,
}: { icon?: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="surface flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon && (
        <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-[var(--sunken)] text-[var(--ink-3)]">
          {icon}
        </div>
      )}
      <h3 className="text-[15px] font-semibold text-[var(--ink)]">{title}</h3>
      {description && (
        <p className="mt-2 max-w-md text-sm leading-relaxed text-[var(--ink-3)]">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/** โครงร่างระหว่างโหลด — บอกล่วงหน้าว่ากำลังจะมีอะไรมา ดีกว่าวงกลมหมุนกลางจอ */
export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={cx('skeleton', className)} style={style} aria-hidden="true" />;
}

export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <div className="surface divide-y divide-[var(--line-soft)]" role="status" aria-label="กำลังโหลด">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3.5">
          <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5" style={{ width: `${55 + ((i * 13) % 30)}%` }} />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 3 }: { count?: number }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" role="status" aria-label="กำลังโหลด">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="surface p-5">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="mt-2.5 h-3 w-1/3" />
          <div className="mt-5 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((n) => <Skeleton key={n} className="h-14 rounded-lg" />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Loading({ label = 'กำลังโหลด…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-[var(--ink-3)]" role="status">
      <Spinner />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'เกิดข้อผิดพลาด';
  return (
    <div className="flex gap-3 rounded-[14px] border border-[color-mix(in_srgb,var(--bad)_35%,transparent)] bg-[var(--bad-soft)] px-4 py-3.5">
      <IconWarning size={18} className="mt-0.5 shrink-0 text-[var(--bad)]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--bad)]">{message}</p>
        {onRetry && <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>ลองใหม่</Button>}
      </div>
    </div>
  );
}

const ACCENT: Record<Tone, string> = {
  gray: 'var(--ink-3)', blue: 'var(--brand)', green: 'var(--ok)',
  amber: 'var(--warn)', rose: 'var(--bad)', violet: 'var(--info)',
};

export function Stat({
  label, value, sub, tone = 'blue', icon, delta,
}: {
  label: string; value: ReactNode; sub?: ReactNode; tone?: Tone;
  icon?: ReactNode; delta?: { value: number; label?: string };
}) {
  return (
    <div
      className="stat-accent surface relative overflow-hidden p-5"
      style={{ ['--accent' as string]: ACCENT[tone] }}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[.06em] text-[var(--ink-3)]">{label}</p>
        {icon && <span className="shrink-0 text-[var(--ink-3)]">{icon}</span>}
      </div>
      <p className="mt-2.5 text-[28px] font-bold leading-none tabular-nums tracking-tight text-[var(--ink)]">
        {value}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--ink-3)]">
        {delta !== undefined && (
          <span className={cx(
            'font-semibold tabular-nums',
            delta.value > 0 ? 'text-[var(--ok)]' : delta.value < 0 ? 'text-[var(--bad)]' : '',
          )}>
            {delta.value > 0 ? '+' : ''}{delta.value}%{delta.label ? ` ${delta.label}` : ''}
          </span>
        )}
        {sub && <span>{sub}</span>}
      </div>
    </div>
  );
}

// ------------------------------ Modal ------------------------------

export function Modal({
  open, onClose, title, children, footer, size = 'md',
}: {
  open: boolean; onClose: () => void; title: string; children: ReactNode;
  footer?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';   // กันหน้าหลังเลื่อนทะลุ modal บนมือถือ
    ref.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[rgb(8_10_14/.55)] backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}
        className={cx('raised relative flex max-h-[88vh] w-full animate-fade-in flex-col overflow-hidden', widths[size])}
      >
        <div className="flex items-center justify-between gap-4 border-b border-[var(--line)] px-5 py-4">
          <h2 className="text-[15px] font-semibold text-[var(--ink)]">{title}</h2>
          <button
            onClick={onClose} aria-label="ปิด"
            className="rounded-lg p-1.5 text-[var(--ink-3)] transition-colors hover:bg-[var(--sunken)] hover:text-[var(--ink)]"
          >
            <IconClose size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-[var(--line)] bg-[var(--sunken)] px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open, onClose, onConfirm, title, message, confirmLabel = 'ยืนยัน', danger, loading,
}: {
  open: boolean; onClose: () => void; onConfirm: () => void;
  title: string; message: ReactNode; confirmLabel?: string; danger?: boolean; loading?: boolean;
}) {
  return (
    <Modal
      open={open} onClose={onClose} title={title} size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>ยกเลิก</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="flex gap-3.5">
        {danger && (
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--bad-soft)] text-[var(--bad)]">
            <IconWarning size={18} />
          </span>
        )}
        <div className="text-sm leading-relaxed text-[var(--ink-2)]">{message}</div>
      </div>
    </Modal>
  );
}

// ------------------------------ Toast ------------------------------

interface Toast { id: number; message: string; tone: 'success' | 'error' | 'info' }
const ToastCtx = createContext<(message: string, tone?: Toast['tone']) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast['tone'] = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);

  const accent: Record<Toast['tone'], string> = {
    success: 'var(--ok)', error: 'var(--bad)', info: 'var(--brand)',
  };

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        role="status" aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="raised pointer-events-auto flex animate-fade-in items-start gap-2.5 px-4 py-3 text-sm text-[var(--ink)]"
            style={{ borderLeft: `3px solid ${accent[t.tone]}` }}
          >
            <span className="mt-0.5 shrink-0" style={{ color: accent[t.tone] }}>
              {t.tone === 'error' ? <IconWarning size={16} /> : <IconCheck size={16} />}
            </span>
            <span className="leading-relaxed">{t.message}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ------------------------------ Misc ------------------------------

export function CopyButton({
  text, label = 'คัดลอก', copiedLabel = 'คัดลอกแล้ว', size = 'sm', variant = 'secondary', className,
}: { text: string; label?: string; copiedLabel?: string; size?: Size; variant?: Variant; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size={size} variant={copied ? 'primary' : variant} className={className}
      icon={copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // clipboard API ต้องการ https หรือ localhost — ถอยไปวิธีเดิมเมื่อใช้ไม่ได้
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.cssText = 'position:fixed;opacity:0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }}
    >
      {copied ? copiedLabel : label}
    </Button>
  );
}

/**
 * แท็บที่เลื่อนได้เมื่อพื้นที่ไม่พอ
 * เดิมแท็บสุดท้ายโดนตัดครึ่งเมื่อคอลัมน์แคบ (เจอในหน้าตั้งค่า widget)
 * จึงใส่เงาบอกทิศทางไว้ให้รู้ว่ายังเลื่อนต่อได้
 */
export function Tabs({
  tabs, active, onChange,
}: { tabs: { id: string; label: string; badge?: ReactNode }[]; active: string; onChange: (id: string) => void }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false });

  const sync = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setEdge({
      left: el.scrollLeft > 2,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    sync();
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync, tabs.length]);

  return (
    <div className="relative border-b border-[var(--line)]">
      {edge.left && (
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[var(--bg)] to-transparent" />
      )}
      {edge.right && (
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[var(--bg)] to-transparent" />
      )}
      <div
        ref={scroller} onScroll={sync} role="tablist"
        className="-mb-px flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t) => {
          const on = active === t.id;
          return (
            <button
              key={t.id} role="tab" aria-selected={on}
              onClick={() => onChange(t.id)}
              className={cx(
                'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-2.5',
                'text-sm font-medium transition-colors',
                on
                  ? 'border-[var(--brand)] text-[var(--brand)]'
                  : 'border-transparent text-[var(--ink-3)] hover:border-[var(--line)] hover:text-[var(--ink)]',
              )}
            >
              {t.label}
              {t.badge !== undefined && t.badge !== null && (
                <span className={cx(
                  'rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
                  on ? 'bg-[var(--brand-soft)] text-[var(--brand)]' : 'bg-[var(--sunken)] text-[var(--ink-3)]',
                )}>
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Pagination({
  page, totalPages, onChange,
}: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-6 flex items-center justify-center gap-3">
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        ก่อนหน้า
      </Button>
      <span className="text-sm tabular-nums text-[var(--ink-3)]">หน้า {page} / {totalPages}</span>
      <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        ถัดไป
      </Button>
    </div>
  );
}

/** แถบความคืบหน้าของโควตา — เปลี่ยนสีเตือนเองเมื่อใกล้เต็ม */
export function Meter({ used, max, className }: { used: number; max: number; className?: string }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, max)) * 100));
  const color = pct > 90 ? 'var(--bad)' : pct > 70 ? 'var(--warn)' : 'var(--brand)';
  return (
    <div
      className={cx('h-1.5 overflow-hidden rounded-full bg-[var(--sunken)]', className)}
      role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
    >
      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}
